import { createContext, useContext, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { trackEvent } from "@/components/Analytics";
import { SOLUTIONS } from "@/data/solutions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BookingContext = createContext(null);
export const useBooking = () => useContext(BookingContext);

const STATIC_PAGES = {
  "/": "Forside",
  "/priser": "Priser",
  "/om": "Om",
  "/handelsbetingelser": "Handelsbetingelser",
  "/privatlivspolitik": "Privatlivspolitik",
};

function pageLabel(pathname) {
  if (STATIC_PAGES[pathname]) return STATIC_PAGES[pathname];
  const m = pathname.match(/^\/losninger\/(.+)$/);
  if (m) {
    const sol = SOLUTIONS.find((s) => s.slug === m[1]);
    return sol ? `Løsning: ${sol.navTitle}` : "Løsningsside";
  }
  return pathname;
}

export function BookingProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const location = useLocation();

  const openBooking = useCallback(() => {
    setForm({ name: "", email: "", phone: "" });
    setDone(false);
    setOpen(true);
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Udfyld navn og email");
    setSaving(true);
    try {
      await api.post("/leads", { ...form, source_page: pageLabel(location.pathname) });
      trackEvent("lead_oprettet", { source_page: pageLabel(location.pathname) });
      setDone(true);
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <BookingContext.Provider value={{ openBooking }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="booking-dialog">
          {done ? (
            <div className="text-center py-6">
              <CheckCircle2 className="h-12 w-12 mx-auto text-foreground mb-5" strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-medium">Tak for din henvendelse!</h2>
              <p className="text-muted-foreground mt-2">Vi vender tilbage til dig hurtigst muligt.</p>
              <Button className="mt-6" onClick={() => setOpen(false)} data-testid="booking-close-btn">Luk</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Vi glæder os til at tale med dig</DialogTitle>
                <DialogDescription>Udfyld dine oplysninger - så vender vi tilbage til dig.</DialogDescription>
              </DialogHeader>
              <div className="flex items-start gap-3 rounded-xl border border-border bg-secondary/50 p-4">
                <span className="relative inline-flex h-1.5 w-1.5 mt-1.5 shrink-0">
                  <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: "#22D3C5", opacity: 0.4 }} />
                  <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#22D3C5" }} />
                </span>
                <p className="text-[13px] text-muted-foreground leading-relaxed">En kort, uforpligtende snak om jeres kommunikation, og hvordan Aicom kan hjælpe. Ingen forberedelse nødvendig.</p>
              </div>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>Navn</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" /></div>
                <div className="space-y-2"><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" /></div>
                <Button className="w-full" onClick={submit} disabled={saving} data-testid="booking-submit-btn">
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send henvendelse
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </BookingContext.Provider>
  );
}
